import {
    Alert,
    Box,
    FormControl,
    Grid,
    Heading,
    ThemeFontSizeVariable,
    ThemeSpaceVariable,
    ToggleButton,
} from "@caspeco/casper-ui-library";
import { ClickIdBuilders } from "logger/click-tracking";
// eslint-disable-next-line caspeco-react/discourage-chakra-import
import { InputGroup } from "@chakra-ui/react";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import { RichTextRenderer } from "components/rich-text-renderer";
import { SafeHTML } from "components/safe-html";
import { useBookingActionsContext, useBookingStateContext } from "hooks/use-booking-state";
import { log, trackEvent } from "logger/logger";
import { PageType } from "logger/page-tracking";
import { usePageTracking } from "logger/use-page-tracking";
import { useStepTracking } from "logger/use-step-tracking";
import { TrackingStepName } from "logger/user-tracking";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Routes } from "routes";
import { resolveRichTextField } from "types/rich-text-field";

function trackLoadingTime() {
    if ("__APP_START_TIME__" in window && "__IS_TRACKED__" in window && !window.__IS_TRACKED__) {
        window.__IS_TRACKED__ = true;
        const now = new Date();
        const loadTime = now.getTime() - window.__APP_START_TIME__.getTime();
        log("App load time: ", loadTime);
        trackEvent("AppLoadTime", {
            ms: loadTime,
        });
    }
}
export const SelectGuests = () => {
    const state = useBookingStateContext();
    const { setGuests } = useBookingActionsContext();
    const { guests: guestsState } = state;
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [toggleLastButton, setToggleLastButton] = useState(false);
    const alertRef = useRef<HTMLDivElement>(null);

    const externalBookingSettings = useExternalBookingSettings().data;
    const { maxWebGuests } = externalBookingSettings?.unitsMetaData ?? { maxWebGuests: 0 };
    const activateInquiry = externalBookingSettings?.activateInquiry ?? false;

    const introductionMessageHeader = externalBookingSettings?.introductionMessageHeader;
    const introductionMessage = externalBookingSettings?.introductionMessage;
    const introductionMessageTemplate =
        externalBookingSettings?.messageTemplates?.introductionMessage;

    // Track guest selection step
    useStepTracking({
        stepName: TrackingStepName.GuestStep,
        autoTrack: true,
    });

    usePageTracking({
        pageName: "SelectGuests",
        pageType: PageType.WizardStep,
        metadata: {
            maxWebGuests,
            activateInquiry,
            hasIntroMessage: Boolean(introductionMessage),
            currentGuests: guestsState,
        },
    });

    useEffect(trackLoadingTime, []);

    useEffect(() => {
        if (toggleLastButton && alertRef.current) {
            // Scroll alert into view when last button is toggled
            alertRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }, [toggleLastButton]);

    const handleBookingRequestRedirect = () => {
        // Navigate to booking request flow
        const bookingRequestPath = `${location.pathname}/${Routes.BOOKING_REQUEST}`;
        void navigate({
            pathname: bookingRequestPath,
            search: location.search,
        });
    };

    return (
        <FormControl key="guest-choice" id="guestChoice">
            {introductionMessageHeader && !introductionMessageTemplate && (
                <Heading as="h1" fontSize={ThemeFontSizeVariable.X2Large}>
                    {introductionMessageHeader}
                </Heading>
            )}
            {(introductionMessageTemplate || introductionMessage) && (
                <Box mt={ThemeSpaceVariable.Medium} mb={ThemeSpaceVariable.Large}>
                    {introductionMessageTemplate ? (
                        <RichTextRenderer
                            content={resolveRichTextField(
                                introductionMessageTemplate,
                                i18n.language
                            )}
                        />
                    ) : (
                        <SafeHTML element="span" html={introductionMessage!} />
                    )}
                </Box>
            )}

            <InputGroup>
                <Grid templateColumns="repeat(4, 1fr)" gap={ThemeSpaceVariable.Small} width="100%">
                    {[...Array(maxWebGuests).keys()]
                        .map((n) => n + 1)
                        .map((guests) => {
                            return (
                                <ToggleButton
                                    key={`guestsBtn${guests}`}
                                    size="lg"
                                    w="full"
                                    onClick={() => setGuests?.(guests)}
                                    isPressed={Boolean(guestsState && guests === maxWebGuests + 1)}
                                    gridColumn={"span 1"}
                                    data-track-click={ClickIdBuilders.selectGuest(guests)}
                                >
                                    {`${guests}`}
                                </ToggleButton>
                            );
                        })}
                    <ToggleButton
                        key={`moreThanMaxGuests${maxWebGuests + 1}`}
                        size="lg"
                        w="full"
                        onClick={() => {
                            if (activateInquiry) {
                                handleBookingRequestRedirect();
                            } else {
                                setToggleLastButton(!toggleLastButton);
                            }
                        }}
                        isPressed={toggleLastButton}
                        gridColumn={"span 4"}
                        data-track-click="select-guest-overflow"
                    >
                        {activateInquiry
                            ? t("bookingRequest.createRequest", {
                                  numberOfGuests: maxWebGuests + 1,
                              })
                            : `${maxWebGuests + 1}+`}
                    </ToggleButton>
                </Grid>
            </InputGroup>
            {toggleLastButton && (
                <Box mt={ThemeSpaceVariable.Medium} ref={alertRef}>
                    <Alert type="info" iconVerticalAlign={"flex-start"}>
                        <SafeHTML
                            element="div"
                            html={
                                externalBookingSettings?.unitsMetaData.messageExceededGuests ?? ""
                            }
                        />
                    </Alert>
                </Box>
            )}
        </FormControl>
    );
};
